import { useState } from "react";
import { Instagram, Facebook, Star } from "lucide-react";
import { CLINIC, MAPS_URLS } from "../../lib/clinic";
import { trackEvent } from "../../lib/analytics";

/**
 * Social icons — "muted by default, brand colour on hover" (Option C).
 *
 * - Default: neutral / subtle (blends with the surrounding layout).
 * - Hover:   reveals the native brand colour (Instagram gradient / Facebook blue).
 * - `tone="dark"`  → designed for dark backgrounds (footer): translucent white base.
 * - `tone="light"` → designed for light backgrounds (header): subtle slate base.
 * - Tracking: GA4 `click_social` with `{ source, location, network }`.
 * - Always opens in a new tab with `rel="noopener noreferrer"`.
 */
const INSTAGRAM_GRADIENT =
  "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)";
const FACEBOOK_BLUE = "#1877F2";
const GOOGLE_GOLD = "#F4B400"; // Google brand yellow (matches our gold-400 vibe)

export default function SocialIcons({
  size = 36,
  location = "header",
  tone = "light",
  onItemClick,
  className = "",
}) {
  const dim = `${size}px`;
  const iconSize = Math.round(size * 0.48);
  const isDark = tone === "dark";
  const [hovered, setHovered] = useState(null); // "instagram" | "facebook" | null

  const handle = (network) => {
    trackEvent("click_social", { source: location, location, network });
    if (onItemClick) onItemClick(network);
  };

  const mutedStyle = isDark
    ? { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.85)" }
    : { background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#64748b" };

  const HOVER_BG = {
    instagram: INSTAGRAM_GRADIENT,
    facebook: FACEBOOK_BLUE,
    google_reviews: GOOGLE_GOLD,
  };

  const styleFor = (network) => {
    const isHovered = hovered === network;
    if (isHovered) {
      return {
        width: dim,
        height: dim,
        background: HOVER_BG[network],
        border: "1px solid transparent",
        color: "#ffffff",
      };
    }
    return { width: dim, height: dim, ...mutedStyle };
  };

  return (
    <div
      className={`inline-flex items-center gap-2.5 ${className}`}
      data-testid={`social-icons-${location}`}
      aria-label="Siga-nos"
    >
      <a
        href={CLINIC.socials.instagram}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram — Instituto Clínico do Barlavento"
        onClick={() => handle("instagram")}
        onMouseEnter={() => setHovered("instagram")}
        onMouseLeave={() => setHovered(null)}
        onFocus={() => setHovered("instagram")}
        onBlur={() => setHovered(null)}
        data-testid={`social-${location}-instagram`}
        style={styleFor("instagram")}
        className="inline-flex items-center justify-center rounded-full transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400"
      >
        <Instagram size={iconSize} strokeWidth={1.8} />
      </a>
      <a
        href={CLINIC.socials.facebook}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook — Instituto Clínico do Barlavento"
        onClick={() => handle("facebook")}
        onMouseEnter={() => setHovered("facebook")}
        onMouseLeave={() => setHovered(null)}
        onFocus={() => setHovered("facebook")}
        onBlur={() => setHovered(null)}
        data-testid={`social-${location}-facebook`}
        style={styleFor("facebook")}
        className="inline-flex items-center justify-center rounded-full transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400"
      >
        <Facebook size={iconSize} strokeWidth={2} fill="currentColor" />
      </a>
      <a
        href={MAPS_URLS.writeReview}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Avalie-nos no Google — Instituto Clínico do Barlavento"
        onClick={() => handle("google_reviews")}
        onMouseEnter={() => setHovered("google_reviews")}
        onMouseLeave={() => setHovered(null)}
        onFocus={() => setHovered("google_reviews")}
        onBlur={() => setHovered(null)}
        data-testid={`social-${location}-google-reviews`}
        style={styleFor("google_reviews")}
        className="inline-flex items-center justify-center rounded-full transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400"
      >
        <Star size={iconSize} strokeWidth={2} fill="currentColor" />
      </a>
    </div>
  );
}
